import React, { useEffect, useRef, useState } from 'react';

// Simple Snake overlay that appears when the SNAKE key sequence is entered
export default function SnakeOverlay() {
  const SEQUENCE = ['S', 'N', 'A', 'K', 'E'];
  const MAX_MS = 3000; // 3 seconds to type sequence

  // --- Tweakable gameplay constants ---
  // Change these to adjust behavior
  const CELL_SIZE = 20; // px per cell
  const STEP_MS = 100; // millisecond between snake steps (snake speed)
  const FOOD_INITIAL_COUNT = 5; // how many food items to spawn at start
  const FOOD_MAX_COUNT = 50; // maximum simultaneous food items
  const FOOD_SPAWN_INTERVAL_MS = 3000; // how often (ms) to spawn additional food
  // ------------------------------------

  const [visible, setVisible] = useState(false);
  const seqIndex = useRef(0);
  const startTime = useRef(0);

  // Game refs
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const snakeRef = useRef([]);
  const dirRef = useRef({ x: 1, y: 0 });
  const foodRef = useRef([]); // now an array of food items
  const lastStepRef = useRef(0);
  const cellRef = useRef(CELL_SIZE);
  const colsRowsRef = useRef({ cols: 0, rows: 0 });
  const runningRef = useRef(false);
  const spawnIntervalRef = useRef(null);

  // Helper to reset sequence
  const resetSeq = () => {
    seqIndex.current = 0;
    startTime.current = 0;
  };

  useEffect(() => {
    const onKey = (e) => {
      // If overlay is visible, let the game handle keys
      if (visible) return;

      // Only accept letter keys for the sequence
      const key = (e.key || '').toUpperCase();
      if (!/^[A-Z]$/.test(key)) return;

      // Must have nothing focused (no input/select/textarea) and no selection
      const active = document.activeElement;
      const noFocus = !active || active.tagName === 'BODY' || active === document.documentElement;
      const selectionEmpty = (window.getSelection && window.getSelection().toString() === '') || true;
      if (!noFocus || !selectionEmpty) {
        resetSeq();
        return;
      }

      const now = Date.now();
      if (seqIndex.current === 0) {
        // start
        if (key === SEQUENCE[0]) {
          seqIndex.current = 1;
          startTime.current = now;
        }
        return;
      }

      // If too much time has passed since start, reset
      if (now - startTime.current > MAX_MS) {
        resetSeq();
        // maybe this key is the start
        if (key === SEQUENCE[0]) {
          seqIndex.current = 1;
          startTime.current = now;
        }
        return;
      }

      // Check next expected
      if (key === SEQUENCE[seqIndex.current]) {
        seqIndex.current += 1;
        if (seqIndex.current >= SEQUENCE.length) {
          // triggered
          resetSeq();
          setVisible(true);
        }
      } else {
        // wrong key: reset
        resetSeq();
        if (key === SEQUENCE[0]) {
          seqIndex.current = 1;
          startTime.current = now;
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  // Initialize/cleanup game when visible changes
  useEffect(() => {
    if (!visible) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // compute cols/rows based on cell size
      const cell = cellRef.current;
      colsRowsRef.current = { cols: Math.floor(canvas.width / cell), rows: Math.floor(canvas.height / cell) };
    };

    const isOccupied = (x, y) => {
      // check snake
      for (let seg of snakeRef.current) {
        if (seg.x === x && seg.y === y) return true;
      }
      // check existing food
      for (let f of foodRef.current) {
        if (f.x === x && f.y === y) return true;
      }
      return false;
    };

    const spawnOneFood = () => {
      const { cols, rows } = colsRowsRef.current;
      if (cols <= 0 || rows <= 0) return;
      // try a bunch of times to find an empty cell
      for (let i = 0; i < 50; i++) {
        const fx = Math.floor(Math.random() * cols);
        const fy = Math.floor(Math.random() * rows);
        if (!isOccupied(fx, fy)) {
          foodRef.current.push({ x: fx, y: fy });
          return;
        }
      }
      // fallback: if can't find empty spot, just push a random one
      foodRef.current.push({ x: Math.floor(Math.random() * cols), y: Math.floor(Math.random() * rows) });
    };

    const ensureInitialFood = () => {
      foodRef.current = [];
      const count = Math.min(FOOD_INITIAL_COUNT, FOOD_MAX_COUNT);
      for (let i = 0; i < count; i++) spawnOneFood();
    };

    const startFoodSpawner = () => {
      if (spawnIntervalRef.current) clearInterval(spawnIntervalRef.current);
      spawnIntervalRef.current = setInterval(() => {
        if (!runningRef.current) return;
        if (foodRef.current.length < FOOD_MAX_COUNT) {
          spawnOneFood();
        }
      }, FOOD_SPAWN_INTERVAL_MS);
    };

    const stopFoodSpawner = () => {
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
        spawnIntervalRef.current = null;
      }
    };

    const startGame = () => {
      const { cols, rows } = colsRowsRef.current;
      const midX = Math.floor(cols / 2);
      const midY = Math.floor(rows / 2);
      snakeRef.current = [
        { x: midX - 1, y: midY },
        { x: midX, y: midY },
      ];
      dirRef.current = { x: 1, y: 0 };
      ensureInitialFood();
      runningRef.current = true;
      lastStepRef.current = performance.now();
      startFoodSpawner();
      animationRef.current = requestAnimationFrame(loop);
    };

    const stopGame = () => {
      runningRef.current = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      stopFoodSpawner();
    };

    const onKeyDown = (e) => {
      // arrow keys or WASD
      const k = e.key;
      if (k === 'ArrowUp' || k === 'w' || k === 'W') {
        if (dirRef.current.y === 0) dirRef.current = { x: 0, y: -1 };
        e.preventDefault();
      }
      if (k === 'ArrowDown' || k === 's' || k === 'S') {
        if (dirRef.current.y === 0) dirRef.current = { x: 0, y: 1 };
        e.preventDefault();
      }
      if (k === 'ArrowLeft' || k === 'a' || k === 'A') {
        if (dirRef.current.x === 0) dirRef.current = { x: -1, y: 0 };
        e.preventDefault();
      }
      if (k === 'ArrowRight' || k === 'd' || k === 'D') {
        if (dirRef.current.x === 0) dirRef.current = { x: 1, y: 0 };
        e.preventDefault();
      }
      if (k === 'Escape') {
        // close overlay
        stopGame();
        setVisible(false);
      }
    };

    const draw = () => {
      const cell = cellRef.current;
      // clear
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // draw food
      ctx.fillStyle = '#ff4d4d';
      for (let f of foodRef.current) {
        ctx.fillRect(f.x * cell, f.y * cell, cell, cell);
      }

      // draw snake
      ctx.fillStyle = '#00ff00';
      const s = snakeRef.current;
      for (let i = 0; i < s.length; i++) {
        const seg = s[i];
        ctx.fillRect(seg.x * cell, seg.y * cell, cell - 1, cell - 1);
      }
    };

    const loop = (t) => {
      if (!runningRef.current) return;
      const now = t;
      const delta = now - lastStepRef.current;
      if (delta > STEP_MS) {
        lastStepRef.current = now;
        // move snake
        const head = { ...snakeRef.current[snakeRef.current.length - 1] };
        head.x += dirRef.current.x;
        head.y += dirRef.current.y;

        const { cols, rows } = colsRowsRef.current;
        // wrap-around
        if (head.x < 0) head.x = cols - 1;
        if (head.x >= cols) head.x = 0;
        if (head.y < 0) head.y = rows - 1;
        if (head.y >= rows) head.y = 0;

        // check collision with self
        for (let seg of snakeRef.current) {
          if (seg.x === head.x && seg.y === head.y) {
            // game over -> stop and hide
            stopGame();
            setTimeout(() => setVisible(false), 800);
            return;
          }
        }

        snakeRef.current.push(head);

        // eat food? find index of food eaten
        let ateIndex = -1;
        for (let i = 0; i < foodRef.current.length; i++) {
          const f = foodRef.current[i];
          if (f.x === head.x && f.y === head.y) {
            ateIndex = i;
            break;
          }
        }

        if (ateIndex !== -1) {
          // remove eaten food
          foodRef.current.splice(ateIndex, 1);
          // spawn a replacement immediately if under max (keeps game lively)
          if (foodRef.current.length < FOOD_MAX_COUNT) spawnOneFood();
        } else {
          // remove tail
          snakeRef.current.shift();
        }

        draw();
      }
      animationRef.current = requestAnimationFrame(loop);
    };

    // start
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('keydown', onKeyDown);

    startGame();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', onKeyDown);
      stopGame();
    };
  }, [visible]);

  if (!visible) return null;

  return (
    // Overlay covers whole page; canvas used for drawing
    <div style={overlayStyle} onMouseDown={() => { /* prevent clicks behind */ }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      {/* small hint */}
      <div style={hintStyle}>Use arrow keys or WASD. Esc to exit.</div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const hintStyle = {
  position: 'fixed',
  right: '16px',
  bottom: '16px',
  color: '#fff',
  background: 'rgba(0,0,0,0.4)',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '13px',
};
